// ImportCard.js
// React 19.2.3 + antd 6.1.3
//
// Требуется зависимость:
//   npm i jszip
//
// Двухэтапный импорт:
// 1) Выбор файла (через antd Upload) + анализ ZIP (сигнатура, export.json, файлы, card/view)
// 2) Выбор "Импорт карточки" / "Импорт визуальных представлений" -> "Импорт" с прогрессбаром
// После успеха: кнопка "Закрыть", "Отмена" скрыта.
//
// Реальный импорт выполнен через Promise-функции:
// - importCardPromise()  -> вызывает importCardApi(...) и обрабатывает success/error
//

import React, { useMemo, useState } from "react";
import { connect } from 'react-redux'
import { Modal, Tooltip, Checkbox, Button, Progress, Typography, Alert, Space, Divider, Upload, Input } from "antd";
import JSZip from "jszip";
import {ntriplesToInsertData, buildDeleteSPARQL} from "./helper"
import settings from '../../virtuoso/settings';
import { GraphExchangeRepository } from '../../redux';

const { Text } = Typography;

function isZipBySignature(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  if (bytes.length < 4) return false;
  const b0 = bytes[0],
    b1 = bytes[1],
    b2 = bytes[2],
    b3 = bytes[3];

  const isPK = b0 === 0x50 && b1 === 0x4b;
  const isLocalHeader = b2 === 0x03 && b3 === 0x04;
  const isEOCD = b2 === 0x05 && b3 === 0x06;
  const isSpanned = b2 === 0x07 && b3 === 0x08;

  return isPK && (isLocalHeader || isEOCD || isSpanned);
}

function normalizeZipPath(p) {
  return String(p || "")
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "")
    .trim();
}

function classifyManifestItem(item) {
  if (item && typeof item === "object") {
    if (item.card) return "card";    
  }
  return "other";
}

function buildErrorMessage(err, fallback = "Ошибка импорта.") {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (err?.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return fallback;
  }
}

function getPurlServerFromIri(iri) {
  if (typeof iri !== "string" || !iri.trim()) return "";
  try {
    return new URL(iri).origin;
  } catch {
    return iri.trim();
  }
}

function replaceAllText(value, source, target) {
  if (typeof value !== "string" || !source) return value;
  return value.split(source).join(target);
}

function replacePurlInManifestItem(item, source, target) {
  if (!item || !source) return item;
  const next = { ...item };
  if (Array.isArray(next.IRIS)) {
    next.IRIS = next.IRIS.map((iri) => replaceAllText(iri, source, target));
  }
  if (next.card) {
    next.card = {
      ...next.card,
      individual: replaceAllText(next.card.individual, source, target),
    };
  }
  return next;
}


// Пример "реального" импорта: один API-вызов на карточку
// Возвращает Promise, который resolve при успехе или reject при ошибке.
async function importCardApi({ manifest, files, graph, deleteBeforeImport, dispatch, replacePurl, sourcePurlServer, targetPurlServer}) {
  for (const file of files) {
    const iri = file.manifestItem?.card?.individual;
    const content = replacePurl
      ? replaceAllText(file.content, sourcePurlServer, targetPurlServer)
      : file.content;

    const sparqlDelete = (deleteBeforeImport && file.manifestItem?.card?.IRIs)
      ? await buildDeleteSPARQL(file.manifestItem?.card?.IRIs)
      : "";  

    const sparqlInsert = await ntriplesToInsertData(content);
    const sparql = sparqlDelete + "\n"+sparqlInsert;
    console.log(sparql);
    const result = await GraphExchangeRepository.importSubjects(graph, sparql);
    if (result !== "OK")
      throw new Error("Ошибка выполнения SPARQL запроса.");
  }
  return { ok: true };
}

// -------------------------

function ImportCard({ open, onClose, title = "Импорт объекта", graph = settings.graph.iri, dispatch}) {
  // stage: choose -> analyzing -> ready -> importing -> done -> error
  const [stage, setStage] = useState("choose");

  const [selectedName, setSelectedName] = useState("");
  const [percent, setPercent] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);

  const [analysis, setAnalysis] = useState(null);
  // analysis = {
  //   fileIndex: Map<string, JSZipObject>
  //   manifest: any[]
  //   items: [{ kind, fileNorm, manifestItem }]
  //   hasCards: boolean
  //   cardsCount: number
  // }

  const [importCard, setImportCard] = useState(true);
  const [deleteBeforeImport, setDeleteBeforeImport] = useState(false);
  const [replacePurl, setReplacePurl] = useState(false);
  const [sourcePurlServer, setSourcePurlServer] = useState("");
  const [targetPurlServer, setTargetPurlServer] = useState("");

  const isAnalyzing = stage === "analyzing";
  const isReady = stage === "ready";
  const isImporting = stage === "importing";
  const isDone = stage === "done";
  const canClose = stage === "choose" || stage === "ready" || stage === "done" || stage === "error";

  function resetAll() {
    setStage("choose");
    setSelectedName("");
    setPercent(0);
    setStatusText("");
    setError(null);
    setAnalysis(null);
    setImportCard(true);
    setDeleteBeforeImport(false);
    setReplacePurl(false);
    setSourcePurlServer("");
    setTargetPurlServer("");
  }

  function closeAndReset() {
    if (!canClose) return;
    onClose();
    resetAll();
  }

  function canImportAnything() {
    if (!analysis) return false;
    const wantCard = importCard && analysis.hasCards;  
    if (!wantCard) return false;
    return true;
  }

  function canPURLChange (){
    if (!analysis) return false;
    if (replacePurl) return !!sourcePurlServer.trim() && !!targetPurlServer.trim();
    return true;
  } 

  async function analyzeZipFile(file) {
    setError(null);
    setStage("analyzing");
    setSelectedName(file?.name || "");
    setPercent(0);
    setStatusText("Подготовка...");

    try {
      setStatusText("Проверка ZIP-архива...");
      const arrayBuffer = await file.arrayBuffer();
      if (!isZipBySignature(arrayBuffer)) throw new Error("Выбранный файл не является ZIP-архивом.");
      setPercent(15);

      setStatusText("Чтение архива...");
      const zip = await JSZip.loadAsync(arrayBuffer);
      const zipFiles = Object.values(zip.files).filter((f) => !f.dir);
      if (zipFiles.length === 0) throw new Error("Архив пуст или не содержит файлов.");
      setPercent(30);

      const fileIndex = new Map();
      for (const f of zipFiles) fileIndex.set(normalizeZipPath(f.name), f);

      setStatusText('Поиск "export.json"...');
      const manifestEntry = fileIndex.get("export.json");
      if (!manifestEntry) throw new Error('В архиве отсутствует обязательный файл "export.json".');
      setPercent(45);

      setStatusText('Чтение "export.json"...');
      const manifestText = await manifestEntry.async("string");

      let manifest;
      try {
        manifest = JSON.parse(manifestText);
      } catch {
        throw new Error('Не удалось распарсить "export.json" как JSON.');
      }
      if (!Array.isArray(manifest)) throw new Error('"export.json" должен содержать JSON-массив записей.');
      setPercent(60);

      setStatusText('Проверка файлов из "export.json"...');
      const items = [];
      const missing = [];

      for (const m of manifest) {
        const fileRaw = m?.file;
        if (typeof fileRaw !== "string" || !fileRaw.trim()) continue;

        const fileNorm = normalizeZipPath(fileRaw);
        const kind = classifyManifestItem(m);

        items.push({ kind, fileNorm, manifestItem: m });
        if (!fileIndex.has(fileNorm)) missing.push(fileNorm);
      }

      if (items.length === 0) throw new Error('В "export.json" не найдено ни одного элемента с полем "file".');

      if (missing.length > 0) {
        const head = missing.slice(0, 10).join(", ");
        const tail = missing.length > 10 ? ` … (+${missing.length - 10})` : "";
        throw new Error(`В архиве отсутствуют файлы, перечисленные в export.json: ${head}${tail}`);
      }

      if (!graph)
        throw new Error(`Граф для импорта не указан`);

      const cardsCount = items.filter((x) => x.kind === "card" &&  x.manifestItem.card.hasOwnProperty("individual") ).length;

      const hasCards = cardsCount > 0;
      const firstIndividual = items.find((x) => x.kind === "card")?.manifestItem?.card?.individual;

      setImportCard(hasCards);
      setSourcePurlServer(getPurlServerFromIri(firstIndividual));
      setTargetPurlServer("");
      setDeleteBeforeImport(false);
      setReplacePurl(false);

      setAnalysis({ fileIndex, manifest, items, hasCards, cardsCount });

      setPercent(0);
      setStatusText("Анализ завершён. Выберите, что импортировать.");
      setStage("ready");
    } catch (e) {
      setStage("error");
      setPercent(0);
      setStatusText("");
      setAnalysis(null);
      setError(buildErrorMessage(e, "Ошибка анализа архива."));
    }
  }

  // Собираем файлы (распаковка) в формате [{name, content, manifestItem}]
  async function extractFiles(items, fileIndex) {
    const out = [];
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const entry = fileIndex.get(it.fileNorm);
      const content = await entry.async("string");
      const manifestItem = replacePurl
        ? replacePurlInManifestItem(it.manifestItem, sourcePurlServer.trim(), targetPurlServer.trim())
        : it.manifestItem;
      out.push({ name: it.fileNorm, content, manifestItem });
    }
    return out;
  }

  // Отдельный promise импорта карточки: распаковать card-файлы -> вызвать API -> обработать успех/ошибку
  async function importCardPromise({ fileIndex, manifest, items }) {
    const cardItems = items.filter((it) => it.kind === "card");
    if (cardItems.length === 0) return;

    setStatusText("Распаковка данных...");
    setPercent(10);
    const files = await extractFiles(cardItems, fileIndex);

    setStatusText("Импорт объекта...");
    setPercent(35);

    try {
      await importCardApi({
        manifest,
        files,
        graph,
        dispatch,
        deleteBeforeImport,
        replacePurl,
        sourcePurlServer: sourcePurlServer.trim(),
        targetPurlServer: targetPurlServer.trim(),
      }); // <-- реальный вызов
      setStatusText("Объект импортирован.");
      setPercent(50);
    } catch (e) {
      throw new Error(`Импорт объекта: ${buildErrorMessage(e)}`);
    }
  }

  async function runImport() {
    if (!analysis) return;

    setError(null);
    setStage("importing");
    setPercent(0);
    setStatusText("Подготовка импорта...");

    try {
      const doCard = importCard && analysis.hasCards;      
      if (!doCard) throw new Error("Ничего не выбрано для импорта.");

      if (replacePurl && (!sourcePurlServer.trim() || !targetPurlServer.trim())) {
        throw new Error("Для замены PURL укажите исходный и целевой PURL сервер.");
      }

      // Вариант: последовательный импорт (сначала карточка, потом представления)
      // Можно поменять местами при необходимости.
      if (doCard) await importCardPromise(analysis);

      setPercent(100);
      setStatusText("Импорт завершён.");
      setStage("done");
    } catch (e) {
      setStage("error");
      setPercent(0);
      setStatusText("");
      setError(buildErrorMessage(e, "Ошибка импорта."));
    }
  }

  const footer = useMemo(() => {
    if (isDone) {
      return (
        <Button type="primary" onClick={closeAndReset}>
          Закрыть
        </Button>
      );
    }

    if (stage === "choose") {
      return (
        <Space>
          <Button onClick={closeAndReset} disabled={!canClose}>
            Отмена
          </Button>

          <Upload
            showUploadList={false}
            multiple={false}
            beforeUpload={(file) => {
              setTimeout(() => analyzeZipFile(file), 0);
              return false;
            }}
          >
            <Button type="primary" loading={isAnalyzing}>
              Выбрать файл
            </Button>
          </Upload>
        </Space>
      );
    }

    if (stage === "ready") {
      return (
        <Space>
          <Button onClick={closeAndReset} disabled={!canClose}>
            Отмена
          </Button>
          <Button type="primary" onClick={runImport} disabled={!canImportAnything() || !canPURLChange()} loading={isImporting}>
            Импорт
          </Button>
        </Space>
      );
    }

    return (
      <Button type="primary" loading={isAnalyzing || isImporting} disabled>
        Импорт
      </Button>
    );
   }, [stage, isDone, isAnalyzing, isImporting, canClose, analysis, importCard, deleteBeforeImport, replacePurl, sourcePurlServer, targetPurlServer])
  ;

  const summary = useMemo(() => {
    if (!analysis) return null;
    return (
      <Text type="secondary">
        Найдено: объектов — {analysis.cardsCount}
      </Text>
    );
  }, [analysis]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={closeAndReset}
      footer={footer}
      maskClosable={canClose}
      closable={canClose}
      destroyOnHidden
    >
      <Divider style={{ margin: "12px 0" }} />

      <div>
        {selectedName ? <Text type="secondary">Файл: {selectedName}</Text> : <Text type="secondary">Файл не выбран</Text>}
      </div>

      {isReady && analysis && (
        <div style={{ marginTop: 12 }}>
          <Text strong>Что импортировать:</Text>

          <div style={{ marginTop: 8 }}>
            <Checkbox checked={importCard} onChange={(e) => setImportCard(e.target.checked)} disabled={!analysis.hasCards || true}>
              Импорт объекта(ов)
            </Checkbox>
          </div>

          <div style={{ marginTop: 8 }}>
            <Checkbox checked={deleteBeforeImport} onChange={(e) => setDeleteBeforeImport(e.target.checked)} disabled={!analysis.hasCards || !importCard}>
              Удалить перед импортом
            </Checkbox>
          </div>

          <div style={{ marginTop: 8 }}>
            <Checkbox checked={replacePurl} onChange={(e) => setReplacePurl(e.target.checked)} disabled={!analysis.hasCards || !importCard}>
              Заменить PURL сервер
            </Checkbox>
          </div>

          {replacePurl && (
            <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
              <Input
                value={sourcePurlServer}
                onChange={(e) => setSourcePurlServer(e.target.value)}
                placeholder="Исходный PURL сервер"
              />
              <Input
                value={targetPurlServer}
                onChange={(e) => setTargetPurlServer(e.target.value)}
                placeholder="Целевой PURL сервер"
              />
            </Space>
          )}

          <div style={{ marginTop: 8 }}>{summary}</div>

          {!canImportAnything() && (
            <div style={{ marginTop: 10 }}>
              <Alert
                type="warning"
                showIcon
                title="Нечего импортировать"
                description="Загрузите другой файл"
              />
            </div>
          )}
          {!canPURLChange() && (
            <div style={{ marginTop: 10 }}>
              <Alert
                type="warning"
                showIcon
                title="Ошибка настройки"
                description="Уберите замену PURL сервера или настройте замену PURL."
              />
            </div>
          )}

        </div>
      )}

      {(isAnalyzing || isImporting || isDone) && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={percent} />
          {!!statusText && (
            <div style={{ marginTop: 8 }}>
              <Text>{statusText}</Text>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 16 }}>
          <Alert type="error" title="Ошибка" description={error} showIcon />
        </div>
      )}

      {isDone && (
        <div style={{ marginTop: 16 }}>
          <Alert type="success" title="Импорт выполнен успешно." showIcon />
        </div>
      )}
    </Modal>
  );
}

const mapStateToProps = (state, ownProps) => {
  const { vadms } = state;
  return {
    ...ownProps,    
    graph: settings.catalog.graph.iri
  };  
}; 

export default connect(mapStateToProps)(ImportCard);